import React, { FC } from 'react';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import CompressIcon from '~Assets/icons/compress.svg';
import ExpandIcon from '~Assets/icons/expand.svg';
import DeleteIcon from '~Assets/icons/delete_outline.svg';
import TimeIcon from '~Assets/icons/schedule.svg';
import TimeEditIcon from '~Assets/icons/clock-edit.svg';
import PersonIcon from '~Assets/icons/person.svg';
import PersonEditIcon from '~Assets/icons/person-edit.svg';
import ServiceTypeChanger from '../ServiceTypeChanger';
import DesiredTime from '~Scenes/POS/Components/Cart/Components/CartHeader/DesiredTime';
import Modal from '~ComponentsImproved/Modal';
import { useButton, useI18n, useLicenses, useSelector } from '~Hooks';
import { useCart } from '~Components/CartProvider';
import { useStorage } from '~Components/Storage';
import { DateTime } from 'luxon';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import ChangeOrderNumber from './ChangeOrderNumber';
import { ActionButton } from './styles';
import useModal from '~ComponentsImproved/Modal/useModal';
import LinkUser from '~Components/LinkUser';
import { GET_USER } from '~Components/LinkUser/api';
import { GetUser, GetUserVariables } from '~Components/LinkUser/__queries__';
import { useQuery } from '@apollo/client';
import MembershipLevelBadge from '~Components/LinkUser/SelectedUser/MembershipLevelBadge';

const Header = styled.div`
  border-top: 1px solid var(--colorBlueGray1100);
  padding: 12px;
  height: 44px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  box-sizing: border-box;
`;

const Qty = styled.div`
  width: 32px;
  margin-right: 8px;
  text-align: center;
`;

const CompressBox = styled(Box)`
  flex-flow: row nowrap;
  margin-left: auto;
`;

const CompressIconWrapper = styled.div`
  padding: 4px;
  background: var(--colorBlueGray1200);
  border-radius: 2px;
  width: fit-content;
`;

const SetValueWrapper = styled(Box)`
  padding: 0px 16px 8px 16px;
`;

const ActionWrapper = styled(Box)`
  padding: 4px 8px 8px 20px;
  height: 64px;
`;

export interface Props {
  /** Only populated in TSR - Used to display the table name in the ordering screen in TSR */
  tableId: string | undefined;
  onClearCart: () => void;
  hideServiceType?: boolean;
  /** TSR - the cheque already has items, so the column header is shown even with an empty cart */
  hasChequeItems?: boolean;
}

const CartHeader: FC<Props> = ({ tableId, onClearCart, hideServiceType, hasChequeItems }) => {
  const { i18n } = useI18n();
  const cart = useCart();
  const licenses = useLicenses();
  const [storage, setStorage] = useStorage();

  const country = useSelector((state) => state.country);

  /** If cart contains this userId we've just fetched it and it should be available in cache */
  const userQuery = useQuery<GetUser, GetUserVariables>(GET_USER, {
    variables: { id: cart.regularUserId || '' },
    skip: !cart.regularUserId,
  });
  const selectedUser = userQuery.data?.User;
  const selectedUserHasMembership = Boolean(
    i18n(selectedUser?.membershipCard?.membershipLevel?.title) ||
      i18n(selectedUser?.membershipLevel?.title),
  );

  const desiredTimeModal = useModal();
  const desiredTimeButton = useButton({
    onClick: () => {
      desiredTimeModal.show();
    },
  });

  const clearCartButton = useButton({
    onClick: () => {
      onClearCart();
      cart.setDesiredTime(null);
    },
  });

  const linkCustomerModal = useModal();
  const linkCustomerButton = useButton({
    onClick: () => {
      linkCustomerModal.show();
    },
  });

  return (
    <>
      <ActionWrapper
        padding="small"
        display="flex"
        alignItems="center"
        justifyContent="space-between">
        {tableId ? (
          <Heading level={2} color={'var(--colorWhite)'}>{`${i18n(
            'POS.Table',
          )} #${tableId}`}</Heading>
        ) : (
          <ChangeOrderNumber />
        )}

        <Box display="flex" alignItems="center">
          {!tableId && country === 'ES' && (
            <ActionButton
              display="flex"
              flexDirection="column"
              alignItems="center"
              {...linkCustomerButton}>
              <Icon
                src={cart.regularUserId ? PersonEditIcon : PersonIcon}
                color={'var(--colorBlueGray900)'}
              />
              <Heading
                level={6}
                color={cart.regularUserId ? 'var(--colorUISand)' : 'var(--colorWhite)'}>
                {i18n('CustomerForCheckout.Member')}
              </Heading>
            </ActionButton>
          )}

          {!tableId && (
            <ActionButton
              display="flex"
              flexDirection="column"
              alignItems="center"
              {...desiredTimeButton}>
              <Icon
                src={cart.desiredTime ? TimeEditIcon : TimeIcon}
                color={'var(--colorBlueGray900)'}
              />
              <Heading
                level={6}
                color={cart.desiredTime ? 'var(--colorUISand)' : 'var(--colorWhite)'}>
                {i18n('DesiredTime.SetTime')}
              </Heading>
            </ActionButton>
          )}

          <ActionButton
            display="flex"
            flexDirection="column"
            alignItems="center"
            {...clearCartButton}>
            <Icon src={DeleteIcon} color={'var(--colorBlueGray900)'} />
            <Heading level={6} color={'var(--colorWhite)'}>
              {i18n('POS.Clear')}
            </Heading>
          </ActionButton>
        </Box>
      </ActionWrapper>

      {cart.desiredTime && (
        <SetValueWrapper display="flex" alignItems="center" gap="xs">
          <Icon src={TimeIcon} color={'var(--colorBlueGray900)'} />
          <Paragraph level={4} color="var(--colorWhite)">
            {i18n('DesiredTime.Title')}:{' '}
            {DateTime.fromISO(cart.desiredTime).toFormat('HH:mm dd/MM')}
          </Paragraph>
        </SetValueWrapper>
      )}

      {cart.regularUserId && (
        <SetValueWrapper display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap="xs">
            <Icon src={PersonIcon} color={'var(--colorBlueGray900)'} />
            <Paragraph level={4} color="var(--colorWhite)">
              {selectedUser?.name}
            </Paragraph>
          </Box>
          {selectedUserHasMembership && (
            <MembershipLevelBadge
              title={
                i18n(selectedUser?.membershipCard?.membershipLevel?.title) ||
                i18n(selectedUser?.membershipLevel?.title)
              }
              primaryColor={
                selectedUser?.membershipCard?.membershipLevel?.primaryColor ||
                selectedUser?.membershipLevel?.primaryColor ||
                'var(--colorBlueGray200)'
              }
            />
          )}
        </SetValueWrapper>
      )}

      {!storage.hideServiceType && cart.serviceType && !hideServiceType && <ServiceTypeChanger />}

      <Header>
        {(cart.items.length > 0 || hasChequeItems) && (
          <>
            <Qty>
              <Heading level={5} color="var(--colorBlueGray600)">
                {i18n('POS.Qty')}
              </Heading>
            </Qty>
            <Heading level={5} color="var(--colorBlueGray600)">
              {i18n('POS.Article')}
            </Heading>
            <CompressBox
              display="flex"
              alignItems="center"
              gap="xs"
              onTouchStart={() => setStorage({ compressedCart: !storage.compressedCart })}>
              <Heading level={6} color={'var(--colorWhite)'}>{`${cart.items.length} ${i18n(
                'General.Items',
              )}`}</Heading>

              <CompressIconWrapper>
                <Icon
                  src={storage.compressedCart ? CompressIcon : ExpandIcon}
                  color="var(--colorBlueGray100)"
                  width="20px"
                  height="20px"
                />
              </CompressIconWrapper>
            </CompressBox>
          </>
        )}
      </Header>

      {/* Modals */}
      <Modal isShowing={desiredTimeModal.isShowing} hide={desiredTimeModal.hide}>
        <DesiredTime onClose={() => desiredTimeModal.hide()} />
      </Modal>

      <LinkUser isShowing={linkCustomerModal.isShowing} onClose={() => linkCustomerModal.hide()} />
    </>
  );
};

export default React.memo(CartHeader);
